<template>
  <div class="grades-sparkline" :style="{ '--sparkline-height': `${props.height}px` }">
    <canvas ref="canvasRef"></canvas>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import Chart from 'chart.js/auto';

const props = defineProps({
  values: {
    type: Array,
    default: () => []
  },
  color: {
    type: String,
    default: '#0199FE'
  },
  height: {
    type: Number,
    default: 73
  }
});

const canvasRef = ref(null);
let chart = null;

function hexToRgb(hexColor) {
  const hex = String(hexColor || '').replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return [1, 153, 254];
  return [
    Number.parseInt(hex.slice(0, 2), 16),
    Number.parseInt(hex.slice(2, 4), 16),
    Number.parseInt(hex.slice(4, 6), 16)
  ];
}

function destroyChart() {
  if (chart) {
    chart.destroy();
    chart = null;
  }
}

async function renderChart() {
  await nextTick();
  if (!canvasRef.value) return;

  const sourceValues = Array.isArray(props.values) ? props.values : [];
  if (!sourceValues.length) {
    destroyChart();
    return;
  }

  const values = sourceValues.length === 1 ? [sourceValues[0], sourceValues[0]] : sourceValues;

  destroyChart();
  const context = canvasRef.value.getContext('2d');
  const rgb = hexToRgb(props.color);

  const fillGradient = context.createLinearGradient(0, 0, 0, 100);
  fillGradient.addColorStop(0, `rgba(${rgb.join(',')}, 0.1)`);
  fillGradient.addColorStop(0.8, 'rgba(255,255,255,0.2)');
  fillGradient.addColorStop(1, 'rgba(255,255,255,0.1)');

  const borderGradient = context.createLinearGradient(0, 0, 0, 50);
  borderGradient.addColorStop(0, `rgba(${rgb.join(',')}, 1)`);
  borderGradient.addColorStop(1, `rgba(${rgb.join(',')}, 1)`);

  chart = new Chart(context, {
    type: 'line',
    data: {
      labels: values.map(() => ''),
      datasets: [
        {
          data: values,
          borderColor: borderGradient,
          backgroundColor: fillGradient,
          borderWidth: 2,
          fill: true,
          tension: 0.4
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 0 },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false }
      },
      scales: {
        x: { display: false },
        y: {
          display: false,
          beginAtZero: true,
          suggestedMin: 0,
          suggestedMax: 11
        }
      },
      elements: {
        point: { radius: 0 }
      }
    }
  });
}

watch(
  () => [props.values, props.color],
  () => {
    renderChart();
  },
  { deep: true }
);

onMounted(() => {
  renderChart();
});

onBeforeUnmount(() => {
  destroyChart();
});
</script>

<style scoped>
.grades-sparkline {
  width: 100%;
  height: var(--sparkline-height, 73px);
}

.grades-sparkline canvas {
  width: 100%;
  height: var(--sparkline-height, 73px);
}
</style>
